import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";

const APPSMITH_COUPON_URL =
  "https://app.appsmith.com/app/coupon/page1-68f07fe528a92f6fcd2caf40?embed=true";
const COUPON_API_URL = "https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/voucher/create";
const MDM_AUTH_KEY = "57b2a9ba-4428-48e4-b137-f8337b68595e";

const initialFormState = {
  code: "",
  distributionType: "",
  name: "",
  type: "cash",
  value: "",
  minSpend: "",
  maxDiscount: "",
  isActive: true,
  maxRedemptions: "",
  validFrom: "",
  validUntil: "",
  products: "",
  tags: "",
};

function CouponManagementBK() {
  const { t } = useTranslation("common");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const handleOpenDialog = () => setOpenCreateDialog(true);
  const handleCloseDialog = () => {
    if (submitting) return;
    setOpenCreateDialog(false);
    setForm(initialFormState);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const formatDateTime = (value) =>
    value ? `${value.length === 16 ? `${value}:00` : value}+08:00` : "";

  const handleSubmit = async () => {
    const requiredFields = [
      "code",
      "distributionType",
      "name",
      "type",
      "value",
      "minSpend",
      "maxDiscount",
      "maxRedemptions",
      "validFrom",
      "validUntil",
    ];

    const missingField = requiredFields.find((field) => !String(form[field] ?? "").trim());
    if (missingField) {
      alert("請完整填寫所有必填欄位");
      return;
    }

    const payload = {
      code: form.code.trim(),
      distributionType: form.distributionType.trim(),
      name: form.name.trim(),
      type: form.type.trim(),
      value: Number(form.value),
      minSpend: Number(form.minSpend),
      maxDiscount: Number(form.maxDiscount),
      isActive: Boolean(form.isActive),
      maxRedemptions: Number(form.maxRedemptions),
      validFrom: formatDateTime(form.validFrom.trim()),
      validUntil: formatDateTime(form.validUntil.trim()),
      products: form.products
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      tags: form.tags
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    };

    if (
      Number.isNaN(payload.value) ||
      Number.isNaN(payload.minSpend) ||
      Number.isNaN(payload.maxDiscount) ||
      Number.isNaN(payload.maxRedemptions)
    ) {
      alert("金額或數量欄位需為數字");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(COUPON_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": MDM_AUTH_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API 回應錯誤：${response.status}`);
      }

      alert("優惠券建立成功");
      setForm(initialFormState);
      setOpenCreateDialog(false);
    } catch (error) {
      console.error("Create coupon failed:", error);
      alert(`建立優惠券失敗：${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.couponManagement")}
                </MDTypography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleOpenDialog}
                  sx={{
                    backgroundColor: "#FF9800",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#FB8C00" },
                  }}
                >
                  {t("actions.createCoupon")}
                </Button>
              </MDBox>
              <MDBox pt={3} px={0} width="100%">
                <iframe
                  src={APPSMITH_COUPON_URL}
                  width="100%"
                  height="800px"
                  frameBorder="0"
                  title="Appsmith Coupon Management"
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openCreateDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("actions.createCoupon")}</DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              label="優惠券代碼"
              name="code"
              value={form.code}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="發放類型"
              name="distributionType"
              value={form.distributionType}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="優惠券名稱"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
            />
            <FormControl fullWidth required sx={{ "& .MuiInputBase-root": { height: "56px" } }}>
              <Select name="type" value={form.type} onChange={handleChange} displayEmpty>
                <MenuItem value="cash">現金券 (cash)</MenuItem>
                <MenuItem value="discount">折扣券 (discount)</MenuItem>
                <MenuItem value="gift">贈品券 (gift)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="優惠券值"
              name="value"
              value={form.value}
              onChange={handleChange}
              required
              type="number"
              fullWidth
            />
            <TextField
              label="最低消費金額"
              name="minSpend"
              value={form.minSpend}
              onChange={handleChange}
              required
              type="number"
              fullWidth
            />
            <TextField
              label="最高折抵金額"
              name="maxDiscount"
              value={form.maxDiscount}
              onChange={handleChange}
              required
              type="number"
              fullWidth
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={handleToggle} name="isActive" />}
              label="是否啟用"
            />
            <TextField
              label="最大使用次數"
              name="maxRedemptions"
              value={form.maxRedemptions}
              onChange={handleChange}
              required
              type="number"
              fullWidth
            />
            <TextField
              label="開始時間"
              helperText="請選擇開始時間"
              name="validFrom"
              value={form.validFrom}
              onChange={handleChange}
              required
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="結束時間"
              helperText="請選擇結束時間"
              name="validUntil"
              value={form.validUntil}
              onChange={handleChange}
              required
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="適用商品 (以逗號分隔)"
              name="products"
              value={form.products}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="標籤 (以逗號分隔)"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              fullWidth
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              backgroundColor: "#FF9800",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#FB8C00" },
            }}
          >
            {submitting ? "送出中..." : "建立"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default CouponManagementBK;
